import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAppearanceSettings, type NameDisplayFormat } from './settings';

export type NameParts = {
  firstName: string;
  lastName: string;
  preferredName?: string | null;
};

/** Format a person's display name honoring preferredName, then the chosen order. */
export function formatEmployeeName(emp: NameParts, format: NameDisplayFormat = 'first_last'): string {
  const preferred = emp.preferredName?.trim();
  if (preferred) return preferred;
  const first = emp.firstName?.trim() ?? '';
  const last = emp.lastName?.trim() ?? '';
  const parts = format === 'last_first' ? [last, first] : [first, last];
  return parts.filter(Boolean).join(' ');
}

const NAME_FORMAT_CACHE_KEY = 'employeeNameDisplayFormat';

/** Last-known format, persisted so a hard reload renders the right order before the query resolves. */
function loadCachedNameFormat(): NameDisplayFormat | undefined {
  try {
    const v = localStorage.getItem(NAME_FORMAT_CACHE_KEY);
    return v === 'first_last' || v === 'last_first' ? v : undefined;
  } catch {
    return undefined;
  }
}
function saveCachedNameFormat(format: NameDisplayFormat): void {
  try {
    localStorage.setItem(NAME_FORMAT_CACHE_KEY, format);
  } catch {
    // ignore storage being unavailable (private mode / quota)
  }
}

/**
 * Returns a formatter bound to the company-wide display format. To avoid a flash of the wrong
 * order on a hard reload, the first paint is seeded from the last-known format in localStorage
 * (falling back to first_last for a never-seen client); the query still refetches and reconciles.
 */
export function useEmployeeNameFormatter(): (emp: NameParts) => string {
  const { data } = useQuery({
    queryKey: ['appearanceSettings'],
    queryFn: async () => {
      const settings = await getAppearanceSettings();
      saveCachedNameFormat(settings.employeeNameDisplayFormat);
      return settings;
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: () => {
      const cached = loadCachedNameFormat();
      return cached ? { employeeNameDisplayFormat: cached } : undefined;
    },
  });
  const format = data?.employeeNameDisplayFormat ?? 'first_last';
  return useCallback((emp: NameParts) => formatEmployeeName(emp, format), [format]);
}
